/**
 * Currency utility functions
 * Task 000i - Currency and Thousand Separator Preferences
 */

/**
 * Formats a number to currency display format
 * @param n - The number to format
 * @param currency - Currency code (e.g., 'IDR', 'USD')
 * @param separator - Thousand separator ('.' or ',')
 * @returns Formatted string
 */
export const formatMoney = (n: number, currency: string = 'IDR', separator: string = '.'): string => {
  const negative = n < 0;
  const abs = Math.abs(n);

  // Determine locale based on separator preference
  // If separator is '.', we use 'id-ID' (1.000)
  // If separator is ',', we use 'en-US' (1,000)
  const locale = separator === '.' ? 'id-ID' : 'en-US';

  const core = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(abs);

  // Currency symbol mapping
  const symbols: Record<string, string> = {
    'IDR': 'Rp',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'SGD': 'S$',
    'MYR': 'RM',
    'INR': '₹'
  };

  const symbol = symbols[currency] || currency;

  // Format: "Rp 1.000" or "$ 1,000"
  // Some currencies might prefer symbol after, but for now we stick to prefix
  return negative ? `- ${symbol} ${core}` : `${symbol} ${core}`;
};

/**
 * Parses a string input to extract numeric value
 * @param input - String input
 * @param separator - Thousand separator used in the input
 * @param allowNegative - Whether to allow negative values
 * @returns Integer value
 */
export const parseMoney = (input: string, separator: string = '.', allowNegative = false): number => {
  // Remove all non-numeric characters except minus and the decimal separator (if we supported decimals)
  // Since we only support integers for now, we just need to handle the thousand separator

  // If separator is '.', remove it. If ',', remove it.
  // We want to end up with a clean number string "1000"

  let cleanInput = input;

  // Remove currency symbols and spaces
  cleanInput = cleanInput.replace(/[^0-9-.,]/g, '');

  // Remove the thousand separator
  if (separator === '.') {
    cleanInput = cleanInput.replace(/\./g, '');
  } else {
    cleanInput = cleanInput.replace(/,/g, '');
  }

  // Handle potential decimal part if user typed it (truncate or round?)
  // For now, let's just take the integer part
  // If there's a decimal separator remaining (e.g. ',' when using '.' as thousand), split
  const decimalSep = separator === '.' ? ',' : '.';
  if (cleanInput.includes(decimalSep)) {
    cleanInput = cleanInput.split(decimalSep)[0];
  }

  const isNegative = cleanInput.startsWith('-');
  const digitsOnly = cleanInput.replace(/[^0-9]/g, '');

  const value = parseInt(digitsOnly, 10) || 0;

  if (isNegative && allowNegative) {
    return -value;
  }

  return value;
};

// Deprecated aliases for backward compatibility
export const formatIDR = (n: number) => formatMoney(n, 'IDR', '.');
export const parseIDR = (input: string, allowNegative = false) => parseMoney(input, '.', allowNegative);
